import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import useViewport from "@hooks/useViewport.js";
import { ApplicationContext } from "@context/ApplicationContext";
import { AuthContext } from "@context/AuthContext";
import { BookSessionContext } from "@context/BookSessionContext.js";
import { VoucherManagerContext } from "@context/VoucherManagerContext";
import AlertMessage from "@components/AlertMessage/index.js";
import Loader from "@components/Ui/Loader";
import ActionBar from "@components/V3/ActionBar";
import StripeCheckoutDrawer from "@components/V3/StripeCheckoutDrawer";
import V3Carousel from "@components/V3/V3Carousel";
import VoucherCheckoutDrawer from "@components/VoucherManager/VoucherCheckoutDrawer";
import VoucherItems from "@components/VoucherManager/VoucherItems";
import { applyFilters } from "@utils/voucherFilters";


import "./style.scss";

const VoucherSearch = ({ type, displayMode = "regular" }) => {
    const { whiteLabel } = useContext(ApplicationContext);
    const { drawerState, openDrawer, closeDrawer } = useContext(AuthContext);
    const { selectedItems, setSelectedItems, setProceedToCheckout, setTermsAccepted } = useContext(BookSessionContext);
    const routerLocation = useLocation();

    const { width } = useViewport();
    const isMobile = width <= 1024;

    const {
        vouchers,
        selectedFilters,
        fetchVoucherBucketSearch,
        setVoucherQuantity,
        hasLoadedVouchers,
        isVoucherBucketSearchLoading
    } = useContext(VoucherManagerContext);

    const [paymentError, setPaymentError] = useState(null);


    useEffect(() => {
        setProceedToCheckout(false);
    }, []);


    useEffect(() => {
        if (!hasLoadedVouchers && !isVoucherBucketSearchLoading) {
            fetchVoucherBucketSearch();
        }
    }, [hasLoadedVouchers, isVoucherBucketSearchLoading, fetchVoucherBucketSearch]);



    useEffect(() => {
        const queryParams = new URLSearchParams(routerLocation.search);
        const status = queryParams.get("status");
        const errors = queryParams.get("errors");

        if (status === "failed" && errors) {
            setPaymentError(errors);

            const clearError = () => setPaymentError(null);
            window.addEventListener("click", clearError, { once: true });

            return () => {
                window.removeEventListener("click", clearError);
            };
        }
    }, [routerLocation.search]);

    const isGiftingMode = displayMode === "gifting";
    const baseVouchers = vouchers;
    const totalQuantity = selectedItems.reduce((carry, item) => carry + item.quantity, 0);

    const displayVouchers = useMemo(() => {
        return applyFilters(baseVouchers, selectedFilters);
    }, [baseVouchers, selectedFilters]);

    const purchasableItemKey = (item) => {
        return item.slug;
    };

    const handleChangeItemQuantity = (selection, idKey, quantity) => {
        const index = selection.findIndex((item) => item.key === idKey);
        let newSelection;

        if (index === -1) {
            const choice = displayVouchers.find(
                (item) => idKey === purchasableItemKey(item)
            );

            if (!choice) {
                console.warn(`Item with key ${idKey} not found in displayVouchers`);
                return;
            }

            newSelection = [
                ...selection,
                {
                    id: idKey,
                    key: idKey,
                    type,
                    short_description: choice.short_description,
                    bucket_id: choice.id,
                    session_inventory_id: choice.session_inventory_id,
                    price_cents: choice.price_cents,
                    display_price_cents: choice.display_price_cents,
                    rrp_price_cents: choice.rrp_price_cents,
                    rebate_cents: choice.rebate_cents,
                    quantity,
                    cinema_group_id: choice.cinema_group_id,
                    is_gifting: isGiftingMode,
                    // Most VM buckets have no ticket type, so only send one when defined.
                    ...(choice.ticket_type_id != null && {
                        ticket_type_id: Number(choice.ticket_type_id),
                    }),
                    cashback_percent: choice.cashback_percent,
                    voucher_cost_cents: choice.voucher_cost_cents,
                },
            ];
        } else {
            newSelection = selection.map((item, i) =>
                i === index ? { ...item, quantity } : item
            );
        }

        setVoucherQuantity(quantity);

        const finalSelection = newSelection.filter((item) => item.quantity > 0);
        setSelectedItems(finalSelection);

        if (finalSelection.length === 0 && drawerState.voucherCheckoutDrawer) {
            setTermsAccepted(false);
            closeDrawer("voucherCheckoutDrawer");
        }
    };

    const title = whiteLabel?.by_line ?? "Select your discount movie vouchers";

    return (
        <>
            <V3Carousel />

            <div className="voucher-search">
                <h1 className="voucher-search__title">{title}</h1>
                <div className={`voucher-search__wrapper ${drawerState.voucherCheckoutDrawer ? "voucher-search__wrapper--open" : ""}`}>
                    {paymentError && <AlertMessage message={paymentError} type="error" />}
                    {isVoucherBucketSearchLoading ? (
                        <div className="voucher-search__loader">
                            <Loader isLoading={isVoucherBucketSearchLoading} />
                        </div>
                    ) : displayVouchers && displayVouchers.length === 0 ? (
                        <div className="voucher-search__empty-state">
                            <p className="paragraph">
                                {isGiftingMode
                                    ? "No gift vouchers available at this time."
                                    : "No vouchers available at this time."}
                            </p>
                        </div>
                    ) : (
                        <div className="voucher-search__results">
                            <VoucherItems
                                items={displayVouchers || []}
                                selectedItems={selectedItems}
                                setQuantity={handleChangeItemQuantity}
                                purchasableItemKey={purchasableItemKey}
                                isGiftingMode={isGiftingMode}
                            />
                        </div>
                    )}
                    <VoucherCheckoutDrawer
                        type={type}
                    />
                </div>
            </div>
            {isMobile && (
                <ActionBar
                    isVisible={totalQuantity > 0}
                    itemCount={totalQuantity}
                    onCheckout={() => openDrawer("voucherCheckoutDrawer")}
                />
            )}
            <StripeCheckoutDrawer />
        </>
    );
};

export default VoucherSearch;
